import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Plus, Trash2, AlertCircle, Key, DollarSign, Pencil, ChevronUp, ChevronDown } from 'lucide-react';

const UserManagement = () => {
  const [users, setUsers] = useState({ writers: [], proofreaders: [], translators: [], handlers: [] });
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [showEditSalaryDialog, setShowEditSalaryDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToResetPassword, setUserToResetPassword] = useState(null);
  const [userToEditSalary, setUserToEditSalary] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [newSalary, setNewSalary] = useState('');
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [updatingSalary, setUpdatingSalary] = useState(false);
  
  // Form state for creating users
  const [createForm, setCreateForm] = useState({
    role: 'handler',
    voornaam: '',
    van: '',
    email: '',
    password: '',
    geboortedatum: '',
    bank_naam: '',
    rekeningnommer: '',
    rekeningtipe: '',
    takkode: '',
    monthly_salary: ''
  });

  useEffect(() => {
    fetchCurrentUser();
    fetchUsers();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Failed to fetch current user:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const [writersRes, proofreadersRes, translatorsRes, handlersRes] = await Promise.all([
        axios.get(`${API}/admin/users?role=writer`),
        axios.get(`${API}/admin/users?role=proofreader`),
        axios.get(`${API}/admin/users?role=translator`),
        axios.get(`${API}/admin/users?role=admin`).catch(() => ({ data: [] }))
      ]);
      
      // Filter handlers from admin users
      const handlers = handlersRes.data.filter(user => user.is_handler === true);
      
      setUsers({ 
        writers: writersRes.data, 
        proofreaders: proofreadersRes.data,
        translators: translatorsRes.data,
        handlers: handlers
      });
    } catch (error) {
      toast.error('Kon nie gebruikers laai nie');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (userId, newStatus) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/status?status=${newStatus}`);
      toast.success('Status opgedateer!');
      fetchUsers();
    } catch (error) {
      toast.error('Kon nie status opdateer nie');
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      const res = await axios.put(`${API}/admin/users/${userId}/role`, { new_role: newRole });
      toast.success(res.data?.message || 'Rol opgedateer!', { duration: 7000 });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon nie rol verander nie');
    }
  };

  const handleCreateUser = async () => {
    // Validation
    if (!createForm.voornaam || !createForm.van || !createForm.email || !createForm.password) {
      toast.error('Vul asseblief alle vereiste velde in');
      return;
    }

    if (createForm.role !== 'handler' && !createForm.geboortedatum) {
      toast.error('Geboortedatum is vereist vir skrywers en proeflees');
      return;
    }

    setCreating(true);
    try {
      // Clean up the form data - only include relevant fields for the role
      const cleanedData = {
        role: createForm.role,
        voornaam: createForm.voornaam,
        van: createForm.van,
        email: createForm.email,
        password: createForm.password
      };
      
      // Add geboortedatum for non-handler roles
      if (createForm.role !== 'handler' && createForm.geboortedatum) {
        cleanedData.geboortedatum = createForm.geboortedatum;
      }
      
      // Add monthly_salary only for handlers and only if it has a value
      if (createForm.role === 'handler' && createForm.monthly_salary) {
        cleanedData.monthly_salary = parseFloat(createForm.monthly_salary);
      }
      
      // Add bank info only for proofreaders and translators
      if (createForm.role === 'proofreader' || createForm.role === 'translator') {
        if (createForm.bank_naam) cleanedData.bank_naam = createForm.bank_naam;
        if (createForm.rekeningnommer) cleanedData.rekeningnommer = createForm.rekeningnommer;
        if (createForm.rekeningtipe) cleanedData.rekeningtipe = createForm.rekeningtipe;
        if (createForm.takkode) cleanedData.takkode = createForm.takkode;
      }
      
      await axios.post(`${API}/admin/users/create`, cleanedData);
      toast.success(`${createForm.role === 'handler' ? 'Hanteerder' : createForm.role === 'writer' ? 'Skrywer' : 'Proefleser'} suksesvol geskep!`);
      setShowCreateDialog(false);
      resetCreateForm();
      fetchUsers();
    } catch (error) {
      // Handle both string errors and Pydantic validation error arrays
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Kon nie gebruiker skep nie';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail) && errorDetail.length > 0) {
        // Pydantic validation errors come as array of objects with 'msg' property
        errorMessage = errorDetail.map(e => e.msg || e.message || String(e)).join(', ');
      }
      
      toast.error(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/users/${userToDelete.id}/delete`);
      toast.success('Gebruiker permanent verwyder');
      setShowDeleteDialog(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' ? errorDetail : 
        (Array.isArray(errorDetail) ? errorDetail.map(e => e.msg || String(e)).join(', ') : 'Kon nie gebruiker verwyder nie');
      toast.error(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteDialog = (user) => {
    setUserToDelete(user);
    setShowDeleteDialog(true);
  };

  const openResetPasswordDialog = (user) => {
    setUserToResetPassword(user);
    setNewPassword('');
    setShowResetPasswordDialog(true);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast.error('Wagwoord moet ten minste 8 karakters lank wees');
      return;
    }

    setResettingPassword(true);
    try {
      const formData = new FormData();
      formData.append('new_password', newPassword);

      await axios.post(`${API}/admin/users/${userToResetPassword.id}/reset-password`, formData);
      
      toast.success(
        `Wagwoord vir ${userToResetPassword.voornaam} ${userToResetPassword.van} suksesvol herstel! ` +
        `Gebruiker sal gevra word om wagwoord te verander by volgende aanmelding.`
      );
      
      setShowResetPasswordDialog(false);
      setUserToResetPassword(null);
      setNewPassword('');
      fetchUsers(); // Refresh to show force_password_change badge
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMsg = typeof errorDetail === 'string' ? errorDetail : 
        (Array.isArray(errorDetail) ? errorDetail.map(e => e.msg || String(e)).join(', ') : 'Kon nie wagwoord herstel nie');
      toast.error(errorMsg);
      console.error('Password reset error:', error);
    } finally {
      setResettingPassword(false);
    }
  };

  const resetCreateForm = () => {
    setCreateForm({
      role: 'handler',
      voornaam: '',
      van: '',
      email: '',
      password: '',
      geboortedatum: '',
      bank_naam: '',
      rekeningnommer: '',
      rekeningtipe: '',
      takkode: '',
      monthly_salary: ''
    });
  };

  // Salary editing functions
  const openEditSalaryDialog = (user) => {
    setUserToEditSalary(user);
    setNewSalary(user.monthly_salary ? user.monthly_salary.toString() : '');
    setShowEditSalaryDialog(true);
  };

  const handleUpdateSalary = async () => {
    if (!userToEditSalary) return;
    
    setUpdatingSalary(true);
    try {
      const formData = new FormData();
      formData.append('salary', newSalary || '0');
      
      await axios.put(`${API}/admin/users/${userToEditSalary.id}/salary`, formData);
      
      const salaryValue = parseFloat(newSalary) || 0;
      toast.success(
        salaryValue > 0 
          ? `Salaris vir ${userToEditSalary.voornaam} opgedateer na R${salaryValue.toLocaleString()}`
          : `Salaris vir ${userToEditSalary.voornaam} verwyder`
      );
      
      setShowEditSalaryDialog(false);
      setUserToEditSalary(null);
      setNewSalary('');
      fetchUsers(); // Refresh to show updated salary
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMsg = typeof errorDetail === 'string' ? errorDetail : 
        (Array.isArray(errorDetail) ? errorDetail.map(e => e.msg || String(e)).join(', ') : 'Kon nie salaris opdateer nie');
      toast.error(errorMsg);
      console.error('Salary update error:', error);
    } finally {
      setUpdatingSalary(false);
    }
  };

  const isMainAdmin = currentUser?.is_main_admin === true;

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Gebruikers</h2>
          <p className="text-gray-600">Bestuur gebruikers van die platform</p>
        </div>
        
        {isMainAdmin && (
          <Button 
            onClick={() => setShowCreateDialog(true)}
            className="bg-orange-500 hover:bg-orange-600"
          >
            <Plus className="h-4 w-4 mr-2" />
            Voeg Gebruiker By
          </Button>
        )}
      </div>

      <Tabs defaultValue="writers">
        <TabsList className={`grid w-full ${isMainAdmin ? 'grid-cols-4' : 'grid-cols-3'}`}>
          <TabsTrigger value="writers">Skrywers ({users.writers.length})</TabsTrigger>
          <TabsTrigger value="proofreaders">Proeflees ({users.proofreaders.length})</TabsTrigger>
          <TabsTrigger value="translators">Vertalers ({users.translators.length})</TabsTrigger>
          {isMainAdmin && (
            <TabsTrigger value="handlers">Hanteerders ({users.handlers.length})</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="writers" className="space-y-4 mt-6">
          {users.writers.length === 0 ? (
            <Card><CardContent className="py-12 text-center"><p className="text-gray-500">Geen skrywers nie</p></CardContent></Card>
          ) : (
            users.writers.map((user) => (
              <UserCard 
                key={user.id} 
                user={user} 
                onStatusChange={handleStatusChange}
                onDelete={openDeleteDialog}
                onResetPassword={openResetPasswordDialog}
                onChangeRole={handleChangeRole}
                currentUserId={currentUser?.id}
                showBooks
                isMainAdmin={isMainAdmin}
              />
            ))
          )}
        </TabsContent>

        <TabsContent value="proofreaders" className="space-y-4 mt-6">
          {users.proofreaders.length === 0 ? (
            <Card><CardContent className="py-12 text-center"><p className="text-gray-500">Geen proeflees nie</p></CardContent></Card>
          ) : (
            users.proofreaders.map((user) => (
              <UserCard 
                key={user.id} 
                user={user} 
                onStatusChange={handleStatusChange}
                onDelete={openDeleteDialog}
                onResetPassword={openResetPasswordDialog}
                onChangeRole={handleChangeRole}
                currentUserId={currentUser?.id}
                showBankDetails
                isMainAdmin={isMainAdmin}
              />
            ))
          )}
        </TabsContent>

        <TabsContent value="translators" className="space-y-4 mt-6">
          {users.translators.length === 0 ? (
            <Card><CardContent className="py-12 text-center"><p className="text-gray-500">Geen vertalers nie</p></CardContent></Card>
          ) : (
            users.translators.map((user) => (
              <UserCard 
                key={user.id} 
                user={user} 
                onStatusChange={handleStatusChange}
                onDelete={openDeleteDialog}
                onResetPassword={openResetPasswordDialog}
                onChangeRole={handleChangeRole}
                currentUserId={currentUser?.id}
                showBankDetails
                showRejectionCount
                isMainAdmin={isMainAdmin}
              />
            ))
          )}
        </TabsContent>

        {isMainAdmin && (
          <TabsContent value="handlers" className="space-y-4 mt-6">
            {users.handlers.length === 0 ? (
              <Card><CardContent className="py-12 text-center"><p className="text-gray-500">Geen hanteerders nie</p></CardContent></Card>
            ) : (
              users.handlers.map((user) => (
                <UserCard 
                  key={user.id} 
                  user={user} 
                  onStatusChange={handleStatusChange}
                  onDelete={openDeleteDialog}
                  onResetPassword={openResetPasswordDialog}
                  onEditSalary={openEditSalaryDialog}
                  onChangeRole={handleChangeRole}
                  currentUserId={currentUser?.id}
                  isHandler
                  isMainAdmin={isMainAdmin}
                />
              ))
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Voeg Nuwe Gebruiker By</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Rol</Label>
              <Select value={createForm.role} onValueChange={(value) => setCreateForm({...createForm, role: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="handler">Hanteerder</SelectItem>
                  <SelectItem value="writer">Skrywer</SelectItem>
                  <SelectItem value="proofreader">Proefleser</SelectItem>
                  <SelectItem value="translator">Vertaler</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                {createForm.role === 'handler' && 'Hanteerders kan alles doen behalwe betalings verwerk en gebruikers skep'}
                {createForm.role === 'writer' && 'Skrywers kan boeke indien en regstellings maak'}
                {createForm.role === 'proofreader' && 'Proeflees kan boeke proeflees en foute lys'}
                {createForm.role === 'translator' && 'Vertalers kan boeke aanvra en vertaal'}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Voornaam *</Label>
                <Input 
                  value={createForm.voornaam}
                  onChange={(e) => setCreateForm({...createForm, voornaam: e.target.value})}
                  placeholder="Voornaam"
                />
              </div>
              <div>
                <Label>Van *</Label>
                <Input 
                  value={createForm.van}
                  onChange={(e) => setCreateForm({...createForm, van: e.target.value})}
                  placeholder="Van"
                />
              </div>
            </div>

            <div>
              <Label>E-pos *</Label>
              <Input 
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({...createForm, email: e.target.value})}
                placeholder="email@example.com"
              />
            </div>

            <div>
              <Label>Tydelike Wagwoord *</Label>
              <Input 
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm({...createForm, password: e.target.value})}
                placeholder="Wagwoord"
              />
              <p className="text-xs text-gray-500 mt-1">Gebruiker sal gevra word om wagwoord te verander by eerste aanmelding</p>
            </div>

            {createForm.role === 'handler' && (
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <Label className="flex items-center gap-2">
                  Maandelikse Salaris (R)
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Opsioneel</span>
                </Label>
                <Input 
                  type="number"
                  min="0"
                  step="100"
                  value={createForm.monthly_salary}
                  onChange={(e) => setCreateForm({...createForm, monthly_salary: e.target.value})}
                  placeholder="0"
                  data-testid="handler-salary-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  As 'n bedrag hier ingevoer word, sal dit op die betalingsbladsy verskyn vir maandelikse betaling.
                </p>
              </div>
            )}

            {createForm.role !== 'handler' && (
              <>
                <div>
                  <Label>Geboortedatum *</Label>
                  <Input 
                    type="date"
                    value={createForm.geboortedatum}
                    onChange={(e) => setCreateForm({...createForm, geboortedatum: e.target.value})}
                  />
                </div>

                {(createForm.role === 'proofreader' || createForm.role === 'translator') && (
                  <>
                    <div className="text-sm font-semibold text-gray-700 pt-2">Bankinligting (Opsioneel)</div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Bank Naam</Label>
                        <Input 
                          value={createForm.bank_naam}
                          onChange={(e) => setCreateForm({...createForm, bank_naam: e.target.value})}
                          placeholder="Bank naam"
                        />
                      </div>
                      <div>
                        <Label>Rekening Tipe</Label>
                        <Select value={createForm.rekeningtipe} onValueChange={(value) => setCreateForm({...createForm, rekeningtipe: value})}>
                          <SelectTrigger>
                            <SelectValue placeholder="Kies..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Spaar">Spaar</SelectItem>
                            <SelectItem value="Tjek">Tjek</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Rekening Nommer</Label>
                        <Input 
                          value={createForm.rekeningnommer}
                          onChange={(e) => setCreateForm({...createForm, rekeningnommer: e.target.value})}
                          placeholder="Rekening nommer"
                        />
                      </div>
                      <div>
                        <Label>Tak Kode</Label>
                        <Input 
                          value={createForm.takkode}
                          onChange={(e) => setCreateForm({...createForm, takkode: e.target.value})}
                          placeholder="Tak kode"
                        />
                      </div>
                    </div>
                  </>
                )}
              </>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowCreateDialog(false);
                  resetCreateForm();
                }}
                disabled={creating}
              >
                Kanselleer
              </Button>
              <Button 
                onClick={handleCreateUser}
                disabled={creating}
                className="bg-orange-500 hover:bg-orange-600"
              >
                {creating ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Besig...</>
                ) : (
                  'Skep Gebruiker'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Bevestig Verwydering
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Is jy seker jy wil <strong>{userToDelete?.voornaam} {userToDelete?.van}</strong> verwyder?
            </p>
            <p className="text-sm text-red-600">
              ⚠️ Hierdie aksie kan NIE ongedaan gemaak word nie. Al die gebruiker se data sal permanent verwyder word.
            </p>
            <div className="flex justify-end gap-3">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowDeleteDialog(false);
                  setUserToDelete(null);
                }}
                disabled={deleting}
              >
                Kanselleer
              </Button>
              <Button 
                variant="destructive"
                onClick={handleDeleteUser}
                disabled={deleting}
              >
                {deleting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Besig...</>
                ) : (
                  <><Trash2 className="h-4 w-4 mr-2" />Verwyder</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPasswordDialog} onOpenChange={setShowResetPasswordDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <Key className="h-5 w-5" />
              Herstel Wagwoord
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Herstel wagwoord vir <strong>{userToResetPassword?.voornaam} {userToResetPassword?.van}</strong>
            </p>
            <div>
              <Label htmlFor="new-password">Nuwe Tydelike Wagwoord</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimum 8 karakters"
                disabled={resettingPassword}
              />
              <p className="text-xs text-gray-500 mt-1">
                Gebruiker sal gedwing word om hierdie wagwoord te verander by volgende aanmelding.
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowResetPasswordDialog(false);
                  setUserToResetPassword(null);
                  setNewPassword('');
                }}
                disabled={resettingPassword}
              >
                Kanselleer
              </Button>
              <Button 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={handleResetPassword}
                disabled={resettingPassword || !newPassword}
              >
                {resettingPassword ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Besig...</>
                ) : (
                  <><Key className="h-4 w-4 mr-2" />Herstel Wagwoord</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Salary Dialog */}
      <Dialog open={showEditSalaryDialog} onOpenChange={setShowEditSalaryDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <DollarSign className="h-5 w-5" />
              Wysig Salaris
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Wysig salaris vir <strong>{userToEditSalary?.voornaam} {userToEditSalary?.van}</strong>
            </p>
            <div>
              <Label htmlFor="new-salary">Maandelikse Salaris (R)</Label>
              <Input
                id="new-salary"
                type="number"
                min="0"
                step="100"
                value={newSalary}
                onChange={(e) => setNewSalary(e.target.value)}
                placeholder="0 = Geen salaris"
                disabled={updatingSalary}
                data-testid="edit-salary-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Voer 0 of laat leeg om die salaris te verwyder.
              </p>
            </div>
            {userToEditSalary?.monthly_salary > 0 && (
              <div className="bg-blue-50 p-3 rounded-lg text-sm">
                <strong>Huidige salaris:</strong> R{userToEditSalary.monthly_salary.toLocaleString()}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowEditSalaryDialog(false);
                  setUserToEditSalary(null);
                  setNewSalary('');
                }}
                disabled={updatingSalary}
              >
                Kanselleer
              </Button>
              <Button 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={handleUpdateSalary}
                disabled={updatingSalary}
                data-testid="save-salary-btn"
              >
                {updatingSalary ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Besig...</>
                ) : (
                  <><DollarSign className="h-4 w-4 mr-2" />Stoor Salaris</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const BOOK_PHASE_STYLE = {
  'Ingedien': 'bg-gray-100 text-gray-700',
  'Wag vir Proeflees': 'bg-blue-100 text-blue-700',
  'Wag vir Goedkeuring': 'bg-indigo-100 text-indigo-700',
  'Wag vir Regmaak': 'bg-orange-100 text-orange-700',
  'Gereed vir Publikasie': 'bg-teal-100 text-teal-700',
  'Gepubliseer': 'bg-green-100 text-green-700',
};

const ROLE_OPTIONS = [
  { value: 'writer', label: 'Skrywer' },
  { value: 'proofreader', label: 'Proefleser' },
  { value: 'translator', label: 'Vertaler' },
  { value: 'handler', label: 'Hanteerder' },
  { value: 'admin', label: 'Hoof Admin' },
];

const UserCard = ({ user, onStatusChange, onDelete, onResetPassword, onEditSalary, onChangeRole, currentUserId, showBankDetails, showRejectionCount, showBooks, isHandler, isMainAdmin }) => {
  const statusConfig = {
    active: { bg: 'bg-green-100', text: 'text-green-800', label: 'Aktief' },
    suspended: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Opgeskort' },
    terminated: { bg: 'bg-red-100', text: 'text-red-800', label: 'Beëindig' },
    pending: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Hangend' },
  };

  const config = statusConfig[user.status] || statusConfig.active;

  const currentRole = user.is_main_admin ? 'admin' : (user.is_handler ? 'handler' : user.role);
  const isSelf = currentUserId && user.id === currentUserId;

  const [booksOpen, setBooksOpen] = React.useState(false);
  const [books, setBooks] = React.useState(null);
  const [booksLoading, setBooksLoading] = React.useState(false);
  const [pendingRole, setPendingRole] = React.useState(null);

  const toggleBooks = async () => {
    const next = !booksOpen;
    setBooksOpen(next);
    if (next && books === null) {
      setBooksLoading(true);
      try {
        const res = await axios.get(`${API}/admin/writers/${user.id}/books`);
        setBooks(res.data || []);
      } catch (e) {
        toast.error('Kon nie boeke laai nie');
        setBooks([]);
      } finally {
        setBooksLoading(false);
      }
    }
  };

  return (
    <Card data-testid={`user-card-${user.id}`}>
      <CardContent className="p-6">
        <div className="flex justify-between items-start">
          <div className="space-y-2 flex-1">
            <div className="flex items-center gap-3">
              <h3 className="text-xl font-bold text-gray-900">{user.voornaam} {user.van}</h3>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.text}`}>
                {config.label}
              </span>
              {user.is_main_admin && (
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-rose-100 text-rose-800">
                  Hoof Admin
                </span>
              )}
              {isHandler && (
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                  Hanteerder
                </span>
              )}
              {showRejectionCount && user.rejection_count > 0 && (
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${user.rejection_count >= 3 ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>
                  Verwerpings: {user.rejection_count}/3
                </span>
              )}
              {user.force_password_change && (
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-800">
                  Moet wagwoord verander
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm text-gray-600">
              <div><strong>E-pos:</strong> {user.email}</div>
              {user.geboortedatum && <div><strong>Geboortedatum:</strong> {user.geboortedatum}</div>}
              {showBankDetails && user.test_score && <div><strong>Toets:</strong> {user.test_score}%</div>}
              {showBankDetails && user.bank_naam && <div><strong>Bank:</strong> {user.bank_naam}</div>}
              {showBankDetails && user.rekeningnommer && <div><strong>Rekening:</strong> {user.rekeningnommer}</div>}
              {isHandler && (
                <div className="col-span-2 mt-2 bg-blue-50 p-2 rounded flex justify-between items-center">
                  <div>
                    <strong className="text-blue-700">Maandelikse Salaris:</strong> 
                    <span className="text-blue-900 font-semibold ml-2">
                      {user.monthly_salary ? `R${user.monthly_salary.toLocaleString()}` : 'Nie gestel nie'}
                    </span>
                  </div>
                  {isMainAdmin && onEditSalary && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-blue-300 text-blue-700 hover:bg-blue-100 h-7 px-2"
                      onClick={() => onEditSalary(user)}
                      data-testid="edit-salary-btn"
                    >
                      <Pencil className="h-3 w-3 mr-1" />
                      Wysig
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Role change (Main Admin only) */}
            {isMainAdmin && onChangeRole && !isSelf && (
              <div className="mt-3 flex items-center gap-2 flex-wrap bg-slate-50 border border-slate-200 rounded-lg p-2">
                <span className="text-xs font-semibold text-slate-600">Verander rol:</span>
                <Select value={currentRole} onValueChange={(v) => { if (v !== currentRole) setPendingRole(v); }}>
                  <SelectTrigger className="h-8 w-44" data-testid={`role-select-${user.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map((r) => (
                      <SelectItem key={r.value} value={r.value} data-testid={`role-option-${r.value}-${user.id}`}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-xs text-slate-400">Gebruiker word outomaties uitgeteken ná verandering</span>
              </div>
            )}

            {/* Writer books + phase */}
            {showBooks && (
              <div className="mt-3">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 border-orange-300 text-orange-700 hover:bg-orange-50"
                  onClick={toggleBooks}
                  data-testid={`toggle-books-${user.id}`}
                >
                  {booksOpen ? <ChevronUp className="h-4 w-4 mr-1" /> : <ChevronDown className="h-4 w-4 mr-1" />}
                  {booksOpen ? 'Versteek boeke' : 'Sien ingediende boeke'}
                </Button>
                {booksOpen && (
                  <div className="mt-2 border rounded-lg p-3 bg-white" data-testid={`books-list-${user.id}`}>
                    {booksLoading ? (
                      <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Laai...</div>
                    ) : (books && books.length > 0) ? (
                      <div className="space-y-2">
                        {books.map((b) => (
                          <div key={b.id} className="flex items-center justify-between gap-3 text-sm border-b last:border-0 pb-2 last:pb-0">
                            <span className="font-medium text-gray-800 truncate">{b.boek_naam}</span>
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${BOOK_PHASE_STYLE[b.status] || 'bg-gray-100 text-gray-700'}`}>
                              {b.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Hierdie skrywer het nog geen boeke ingedien nie.</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            {user.status !== 'active' && (
              <Button size="sm" className="bg-green-500 hover:bg-green-600" onClick={() => onStatusChange(user.id, 'active')}>
                Aktiveer
              </Button>
            )}
            {user.status !== 'suspended' && user.status !== 'terminated' && (
              <Button size="sm" variant="outline" onClick={() => onStatusChange(user.id, 'suspended')}>
                Skors
              </Button>
            )}
            {isMainAdmin && (
              <>
                <Button 
                  size="sm" 
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => onResetPassword(user)}
                  title="Herstel Wagwoord"
                >
                  <Key className="h-4 w-4" />
                </Button>
                <Button 
                  size="sm" 
                  variant="destructive"
                  onClick={() => onDelete(user)}
                  title="Verwyder Gebruiker"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>

      {/* Role change confirmation */}
      <Dialog open={!!pendingRole} onOpenChange={(o) => { if (!o) setPendingRole(null); }}>
        <DialogContent data-testid={`role-confirm-dialog-${user.id}`}>
          <DialogHeader>
            <DialogTitle>Bevestig rolverandering</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>
              Verander <strong>{user.voornaam} {user.van}</strong> se rol na{' '}
              <strong>{ROLE_OPTIONS.find((r) => r.value === pendingRole)?.label}</strong>?
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800">
              Die gebruiker se toegang verander heeltemal na die nuwe rol. Hulle word <strong>outomaties uitgeteken</strong> en moet weer inteken.
              {pendingRole === 'admin' && ' Let wel: "Hoof Admin" gee volle administratiewe toegang.'}
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setPendingRole(null)} data-testid={`role-cancel-${user.id}`}>Kanselleer</Button>
            <Button
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid={`role-confirm-${user.id}`}
              onClick={() => { onChangeRole(user.id, pendingRole); setPendingRole(null); }}
            >
              Ja, verander rol
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default UserManagement;
